<script setup lang="ts">
import { computed } from 'vue'
import {
	BellIcon,
	BuildingOffice2Icon,
	BuildingOfficeIcon,
	CalendarIcon,
	CheckCircleIcon,
	CurrencyDollarIcon,
	EnvelopeIcon,
	IdentificationIcon,
	PhoneIcon,
	ShieldCheckIcon,
	UserGroupIcon,
	UserIcon,
	UsersIcon,
	XCircleIcon,
} from '@heroicons/vue/24/outline'

import { useAuth } from '@/composables/useAuth'
import { useUserContactInfo } from '@/modules/users/composables/useUserContactInfo'
import type { User } from '@/modules/users/interfaces/user'
import { capitalize, formatDate } from '@/utils'

interface Props {
	user: User
}

const props = defineProps<Props>()
const { user: userLogged } = useAuth()

const isInternalUser = computed(() => userLogged.value?.internal === true)
const userComputed = computed(() => props.user)
const { contactInfo, hasEmails, hasPhones } = useUserContactInfo(userComputed)
</script>

<template>
	<div class="divide-y divide-gray-100">
		<!-- Section: Personal -->
		<div class="p-6">
			<h4 class="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
				<IdentificationIcon class="h-4 w-4" />
				Información personal
			</h4>

			<div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
				<!-- Emails -->
				<div class="flex items-start gap-3">
					<EnvelopeIcon class="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					<div class="min-w-0">
						<p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Correos</p>
						<div v-if="hasEmails" class="space-y-1">
							<div
								v-for="(email, i) in contactInfo.emails"
								:key="i"
								class="flex items-center gap-1.5 text-sm text-gray-800"
							>
								<span class="break-all">{{ email.value }}</span>
								<svg
									v-if="email.type === 'primary'"
									class="h-3 w-3 text-yellow-500 flex-shrink-0"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
								</svg>
								<BellIcon v-else class="h-3 w-3 text-gray-400 flex-shrink-0" />
							</div>
						</div>
						<p v-else class="text-sm text-gray-400">Sin correos</p>
					</div>
				</div>

				<!-- WhatsApp -->
				<div class="flex items-start gap-3">
					<PhoneIcon class="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					<div class="min-w-0">
						<p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">WhatsApp</p>
						<div v-if="hasPhones" class="space-y-1">
							<div
								v-for="(phone, i) in contactInfo.phones"
								:key="i"
								class="flex items-center gap-1.5 text-sm text-gray-800"
							>
								<span class="font-mono">{{ phone.formatted || phone.value }}</span>
								<BellIcon class="h-3 w-3 text-gray-400 flex-shrink-0" />
							</div>
						</div>
						<p v-else class="text-sm text-gray-400">Sin WhatsApp</p>
					</div>
				</div>

				<!-- Fecha de nacimiento -->
				<div class="flex items-start gap-3">
					<CalendarIcon class="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					<div>
						<p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Fecha de nacimiento</p>
						<p class="text-sm text-gray-800">
							{{ user.birthDate ? formatDate(user.birthDate) : 'No especificada' }}
						</p>
					</div>
				</div>

				<!-- Género -->
				<div class="flex items-start gap-3">
					<UserIcon class="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					<div>
						<p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Género</p>
						<p class="text-sm text-gray-800 capitalize">
							{{ user.gender ? capitalize(user.gender) : 'No especificado' }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Section: Organizacional (solo externos) -->
		<div v-if="!user.internal" class="p-6">
			<h4 class="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
				<BuildingOfficeIcon class="h-4 w-4" />
				Información organizacional
			</h4>

			<div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
				<div class="flex items-start gap-3">
					<BuildingOffice2Icon class="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					<div>
						<p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Empresa</p>
						<p class="text-sm text-gray-800">{{ user.companyName || 'No asignada' }}</p>
					</div>
				</div>

				<div class="flex items-start gap-3">
					<UsersIcon class="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					<div>
						<p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Departamento</p>
						<p class="text-sm text-gray-800">{{ user.departmentName || 'No asignado' }}</p>
					</div>
				</div>

				<div class="flex items-start gap-3">
					<CurrencyDollarIcon class="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					<div>
						<p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Centro de costo</p>
						<p class="text-sm text-gray-800">{{ user.costCenterName || 'No asignado' }}</p>
					</div>
				</div>

				<div class="flex items-start gap-3">
					<UserGroupIcon class="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
					<div>
						<p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Grupo</p>
						<p class="text-sm text-gray-800">{{ user.groupName || 'No asignado' }}</p>
					</div>
				</div>

				<div class="flex items-start gap-3">
					<CheckCircleIcon v-if="!user.deletedAt" class="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
					<XCircleIcon v-else class="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
					<div>
						<p class="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Estado de acceso</p>
						<p class="text-sm" :class="!user.deletedAt ? 'text-emerald-600' : 'text-red-500'">
							{{ !user.deletedAt ? 'Activo' : 'Inactivo' }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Section: Roles (solo internos) -->
		<div v-if="isInternalUser && user.roles?.length" class="p-6">
			<h4 class="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
				<ShieldCheckIcon class="h-4 w-4" />
				Roles
			</h4>
			<div class="flex flex-wrap gap-2">
				<span
					v-for="(role, i) in user.roles"
					:key="i"
					class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#253C70]/10 text-[#253C70]"
				>
					{{ role }}
				</span>
			</div>
		</div>
	</div>
</template>
